import {
  Body,
  Box,
  Flex,
  IconButton,
  Image,
  List,
  ListItem,
} from '@hover/blueprint';
import { iPause, iPlay } from '@hover/icons';
import React, { useEffect, useRef, useState } from 'react';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import bedroomImage from 'assets/img/designLandingPage/carousel/bedroom.jpg';
import exteriorImage from 'assets/img/designLandingPage/carousel/exterior.jpg';
import exterior2Image from 'assets/img/designLandingPage/carousel/exterior2.jpg';
import exterior3Image from 'assets/img/designLandingPage/carousel/exterior3.jpg';
import livingRoomImage from 'assets/img/designLandingPage/carousel/livingRoom.jpg';
import livingRoom2Image from 'assets/img/designLandingPage/carousel/livingRoom2.jpg';
import livingRoom3Image from 'assets/img/designLandingPage/carousel/livingRoom3.jpg';

import { CONSTANTS, autoSlide } from './constants';
import { getCardOffset } from './utils';

const InfinityCarousel = () => {
  const t = useTranslate(TranslationNamespace.DESIGNER_LANDING_PAGE);
  const animatedContainerRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  const mediaCards = [
    {
      alt: t('carousel.altText.modernLivingRoom'),
      image: livingRoom2Image,
      label: t('carousel.livingRoom'),
    },
    {
      alt: t('carousel.altText.bedroom'),
      image: bedroomImage,
      label: t('carousel.bedroom'),
    },
    {
      alt: t('carousel.altText.exteriorHome'),
      image: exterior2Image,
      label: t('carousel.exterior'),
    },
    {
      alt: t('carousel.altText.livingRoom'),
      image: livingRoom3Image,
      label: t('carousel.livingRoom'),
    },
    {
      alt: t('carousel.altText.exteriorTwoStory'),
      image: exterior3Image,
      label: t('carousel.exterior'),
    },
    {
      alt: t('carousel.altText.livingRoomNeutral'),
      image: livingRoomImage,
      label: t('carousel.livingRoom'),
    },
    {
      alt: t('carousel.altText.modernRanch'),
      image: exteriorImage,
      label: t('carousel.exterior'),
    },
  ];

  // Cards are duplicated to create illusion of endless scroll animation
  // Duplication value needs to be an even number
  const timesToDuplicateCards = 4;

  const mediaCardsDuplicated = Array.from(
    { length: timesToDuplicateCards },
    () => mediaCards,
  ).flat();

  // Animation speed is based on number of cards
  const animationSpeed = `${
    mediaCardsDuplicated.length * CONSTANTS.speedMultiplier
  }s`;

  // Sets initial position of card container for mobile so that
  // a card or two cards are centered in the window
  useEffect(() => {
    if (animatedContainerRef.current && window.innerWidth) {
      const cardOffset = getCardOffset(window.innerWidth);
      animatedContainerRef.current.scrollLeft = cardOffset;
    }
  }, []);

  return (
    <>
      {/* Animated container */}
      <Flex
        marginTop={950}
        overflow="hidden"
        ref={animatedContainerRef}
        width="100%"
      >
        <Flex
          animation={`${autoSlide} ${animationSpeed} linear infinite forwards`}
          sx={{
            animationPlayState: isPaused
              ? 'paused !important'
              : 'running !important',
          }}
        >
          <List
            aria-label={t('carousel.altText.slideshow')}
            marginY="100"
            pl="0"
          >
            <Flex>
              {/* We need to handle spacing between the cards with margin-right on ListItem, NOT GAP */}
              {/* The extra space is needed to the right of the last card for the animation to be seamless */}
              {mediaCardsDuplicated.map((card, index) => {
                const numberOfOriginalCards = mediaCards?.length;

                const imageHeight = {
                  base: `${CONSTANTS.imageDimensions.base.height}px`,
                  tablet: `${CONSTANTS.imageDimensions.tablet.height}px`,
                };

                const imageWidth = {
                  base: `${CONSTANTS.imageDimensions.base.width}px`,
                  tablet: `${CONSTANTS.imageDimensions.tablet.width}px`,
                };

                return (
                  <ListItem
                    // Hide duplicated cards from screenreaders
                    aria-hidden={
                      index < numberOfOriginalCards ? 'false' : 'true'
                    }
                    key={`${index.toString() + card.label}`}
                    marginRight={CONSTANTS.cardGap}
                  >
                    <Box
                      maxHeight={imageHeight}
                      maxWidth={imageWidth}
                      minHeight={imageHeight}
                      minWidth={imageWidth}
                      position="relative"
                    >
                      <Image
                        alt={card.alt}
                        borderRadius={400}
                        flexShrink={0}
                        maxWidth="100%"
                        objectFit="cover"
                        src={card.image}
                        width="100%"
                      />
                      <Box
                        bottom={400}
                        justifyContent="center"
                        left="0"
                        position="absolute"
                        right="0"
                      >
                        <Box
                          backdropFilter="blur(25px)"
                          background="hsla(0,0%,5%,.5)"
                          borderRadius="round"
                          paddingX={500}
                          paddingY={300}
                        >
                          <Body
                            as="span"
                            color="neutral.0"
                            fontWeight={700}
                            size={200}
                          >
                            {card.label}
                          </Body>
                        </Box>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </Flex>
          </List>
        </Flex>
      </Flex>
      <Flex justifyContent="flex-end" mr={{ base: 600, tablet: 900 }} mt="600">
        <IconButton
          icon={isPaused ? iPlay : iPause}
          label={isPaused ? 'Play' : 'Pause'}
          onClick={() => setIsPaused(!isPaused)}
          size="small"
          variant="secondary"
        />
      </Flex>
    </>
  );
};

export { InfinityCarousel };
